import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
  IonSearchbar,
  IonGrid,
  IonRow,
  IonCol,
  IonImg,
  IonSpinner,
  IonCard,
  IonCardContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/angular/standalone';
import { RouterLink } from '@angular/router';
import { addIcons } from 'ionicons';
import { close, search } from 'ionicons/icons';
import {
  Subject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  from,
  of,
} from 'rxjs';

addIcons({ close, search });

interface UnsplashPhoto {
  id: string;
  urls: {
    thumb: string;
    small: string;
    regular: string;
    full: string;
  };
  alt_description?: string;
  width: number;
  height: number;
}

interface UnsplashSearchResponse {
  results: UnsplashPhoto[];
  total: number;
  total_pages: number;
}

@Component({
  selector: 'crowdctrl-unsplash-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    IonContent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonIcon,
    IonSearchbar,
    IonGrid,
    IonRow,
    IonCol,
    IonImg,
    IonSpinner,
    IonCard,
    IonCardContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
  ],
  templateUrl: './unsplash-search.component.html',
  styleUrl: './unsplash-search.component.css',
})
export class UnsplashSearchComponent implements OnInit, OnDestroy {
  @Input() pageTitle = 'Search Images';
  @Input() defaultHref = '/';
  @Input() unsplashAccessKey = ''; // Must be provided by parent component
  @Output() imageSelected = new EventEmitter<string>();

  searchQuery = '';
  photos: UnsplashPhoto[] = [];
  isLoading = false;
  isLoadingMore = false;
  errorMessage: string | null = null;
  currentPage = 1;
  totalPages = 1;
  hasMore = false;

  private searchSubject = new Subject<string>();
  private searchSubscription: any;

  ngOnInit(): void {
    // Debounce search input
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((query) => {
          if (!query || query.trim().length === 0) {
            this.photos = [];
            this.currentPage = 1;
            this.totalPages = 1;
            this.hasMore = false;
            return of(null);
          }
          return this.searchPhotos(query, 1);
        }),
        catchError((error) => {
          console.error('Search error:', error);
          this.errorMessage = 'Failed to search images. Please try again.';
          this.isLoading = false;
          return of(null);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onSearchInput(event: any): void {
    const query = event.target.value || '';
    this.searchQuery = query;
    this.errorMessage = null;
    this.searchSubject.next(query);
  }

  onSearchSubmit(): void {
    if (this.searchQuery.trim().length > 0) {
      this.searchSubject.next(this.searchQuery);
    }
  }

  private searchPhotos(query: string, page: number): Observable<UnsplashSearchResponse | null> {
    if (!this.unsplashAccessKey) {
      this.errorMessage = 'Unsplash API key not configured';
      return of(null);
    }

    this.isLoading = page === 1;
    this.isLoadingMore = page > 1;

    const url = new URL('https://api.unsplash.com/search/photos');
    url.searchParams.set('query', query);
    url.searchParams.set('page', page.toString());
    url.searchParams.set('per_page', '20');
    url.searchParams.set('orientation', 'squarish'); // Force square images

    return from(
      fetch(url.toString(), {
        headers: {
          Authorization: `Client-ID ${this.unsplashAccessKey}`,
        },
      }),
    ).pipe(
      switchMap((response) => {
        if (!response.ok) {
          throw new Error(`Unsplash API error: ${response.statusText}`);
        }
        return from(response.json());
      }),
      switchMap((data: UnsplashSearchResponse) => {
        if (page === 1) {
          this.photos = data.results;
        } else {
          this.photos = [...this.photos, ...data.results];
        }
        this.currentPage = page;
        this.totalPages = data.total_pages;
        this.hasMore = page < data.total_pages;
        this.isLoading = false;
        this.isLoadingMore = false;
        return of(data);
      }),
      catchError((error) => {
        console.error('Error searching Unsplash:', error);
        this.errorMessage = 'Failed to search images. Please check your API key.';
        this.isLoading = false;
        this.isLoadingMore = false;
        return of(null);
      }),
    );
  }

  loadMore(event: any): void {
    if (this.hasMore && !this.isLoadingMore && this.searchQuery.trim().length > 0) {
      this.searchPhotos(this.searchQuery, this.currentPage + 1).subscribe(() => {
        event.target.complete();
      });
    } else {
      event.target.complete();
    }
  }

  selectImage(photo: UnsplashPhoto): void {
    // Use the regular size for better quality, but still square
    this.imageSelected.emit(photo.urls.regular);
  }

  getImageAlt(photo: UnsplashPhoto): string {
    return photo.alt_description || 'Unsplash image';
  }
}
